/**
 * Utilities for Enalysis report/fieldData (e.g. fieldDataDetail from context).
 */

import { mathUtils } from "./utils";

/** WarningLevel values from API (Warning Flags API returns these strings) */
export const WARNING_LEVEL_NONE = "None";
export const WARNING_LEVEL_MODERATE = "Warning";
export const WARNING_LEVEL_SEVERE = "Severe";
/**
 * Format a cylinder detail value for display with optional rounding.
 * @param {string|number|null|undefined} value
 * @param {number|null} decimals - 0 = whole, 1 = tenth, 2 = hundredth, 3 = thousandth, null = no rounding
 * @returns {string|number}
 */
export function formatValue(value, decimals) {
  if (value === "n/a") return "n/a";
  if (value == undefined || value === "") return "";
  if (decimals == undefined) return String(value);
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return mathUtils.round(n, decimals);
}

/**
 * Rows sourced from report detail cylinder (ReportDetails.Cylinder). getValue(col, detail) receives detail = { cylinder, fieldDataDetail, reportDetail, stage, throw }.
 */
export const CYLINDER_DETAIL_ROWS = [
  {
    key: "cylinderModel",
    getValue: (col, detail) => detail.cylinder.cylinderModel,
    label: "Cylinder Model",
    decimals: null,
    unit: "",
  },
  {
    key: "cylinderMAWP",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder ? "n/a" : detail.cylinder.cylinderMAWP,
    label: "Cylinder MAWP",
    decimals: 0,
    unit: "psig",
  },
  {
    key: "cylinderAction",
    getValue: (col, detail) => detail.cylinder.cylinderAction,
    label: "Cylinder Action",
    decimals: null,
    unit: "",
  },
  {
    key: "cylinderDiameter",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.cylinder.cylinderDiameter,
    label: "Cylinder Diameter",
    decimals: 3,
    unit: "inches",
  },
  {
    key: "rodDiameter",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder ? "n/a" : detail.cylinder.rodDiameter,
    label: "Rod Diameter",
    decimals: 3,
    unit: "inches",
  },
];

/**
 * Metric rows for cylinder details table. getValue(col, detail) receives detail = { cylinder, fieldDataDetail, reportDetail, stage, throw }.
 * fieldDataDetail = fieldData.fieldDataDetail item; reportDetail = report.reportDetails item (same stage/throw); cylinder = reportDetail.cylinder.
 */
export const REPORT_DETAIL_ROWS = [
  {
    key: "suctionTemperature",
    getValue: (col, detail) => detail.fieldDataDetail.suctionTemperature,
    label: "Suction Temperature",
    decimals: 1,
    warningFlag: "suctionTemperature",
    unit: "°F",
  },
  {
    key: "dischargeTemperature",
    getValue: (col, detail) => detail.fieldDataDetail.dischargeTemperature,
    label: "Discharge Temperature",
    decimals: 1,
    warningFlag: "dischargeTemperature",
    unit: "°F",
  },
  {
    key: "expectedDischargeTemperature",
    getValue: (col, detail) => detail.reportDetail.expectedDischargeTemperature,
    label: "Expected Discharge Temp",
    decimals: 1,
    unit: "°F",
  },
  {
    key: "temperatureRise",
    getValue: (col, detail) => {
      const discharge = detail.fieldDataDetail.dischargeTemperature;
      const suction = detail.fieldDataDetail.suctionTemperature;
      return formatValue(Number(discharge) - Number(suction), 1);
    },
    label: "Temperature Rise",
    decimals: 1,
    unit: "°F",
  },
  {
    key: "suctionPressure",
    getValue: (col, detail) => detail.fieldDataDetail.suctionPressure,
    label: "Suction Pressure",
    decimals: 1,
    warningFlag: "suctionPressureMisread",
    unit: "psig",
  },
  {
    key: "dischargePressure",
    getValue: (col, detail) => detail.fieldDataDetail.dischargePressure,
    label: "Discharge Pressure",
    decimals: 1,
    warningFlag: "dischargePressureMisreadOrTooHigh",
    unit: "psig",
  },
  {
    key: "compressionRatio",
    getValue: (col, detail) => detail.reportDetail.compressionRatio,
    label: "Compression Ratio",
    decimals: 1,
    warningFlag: "compressionRatioTooLow",
    unit: "",
  },
  {
    key: "headEndPocketAdjustment",
    getValue: (col, detail) => detail.fieldDataDetail.headEndPocketAdjustment,
    label: "HE Pocket Adjustment",
    decimals: 3,
    unit: "inches",
  },
  {
    key: "headEndSpacers",
    getValue: (col, detail) => detail.fieldDataDetail.headEndSpacers,
    label: "Head End Spacers",
    decimals: 0,
    unit: "Qty",
  },
  {
    key: "crankEndSpacers",
    getValue: (col, detail) => detail.fieldDataDetail.crankEndSpacers,
    label: "Crank End Spacers",
    decimals: 0,
    unit: "Qty",
  },
  {
    key: "runClearanceHeadEnd",
    getValue: (col, detail) => detail.reportDetail.runClearanceHeadEnd,
    label: "Running Clearance HE",
    decimals: 0,
    unit: "%",
  },
  {
    key: "runClearanceCrankEnd",
    getValue: (col, detail) => detail.reportDetail.runClearanceCrankEnd,
    label: "Running Clearance CE",
    decimals: 0,
    unit: "%",
  },
  {
    key: "volumeEfficiencyHeadEnd",
    getValue: (col, detail) => detail.reportDetail.volumeEfficiencyHeadEnd,
    label: "Volumetric Efficiency HE",
    decimals: 0,
    warningFlag: "volumeEfficiencyHeadEndTooLow",
    unit: "%",
  },
  {
    key: "volumeEfficiencyCrankEnd",
    getValue: (col, detail) => detail.reportDetail.volumeEfficiencyCrankEnd,
    label: "Volumetric Efficiency CE",
    decimals: 0,
    warningFlag: "volumeEfficiencyCrankEndTooLow",
    unit: "%",
  },
  {
    key: "blowby",
    getValue: (col, detail) => detail.reportDetail.blowby,
    label: "Blowby",
    decimals: 1,
    warningFlag: "blowbyWarningLevel",
    unit: "%",
  },
  {
    key: "intExtRodLoadCompression",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.intExtRodLoadCompression,
    label: "Rod Load Compression",
    decimals: 0,
    warningFlag: "rodLoadCompressionTooHigh",
    unit: "lbs",
  },
  {
    key: "intExtRodLoadTension",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.intExtRodLoadTension,
    label: "Rod Load Tension",
    decimals: 0,
    warningFlag: "rodLoadTensionTooHigh",
    unit: "lbs",
  },
  {
    key: "totalInternalRodLoad",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.totalInternalRodLoad,
    label: "Total Internal Rod Load",
    decimals: 0,
    unit: "lbs",
  },
  {
    key: "netRodLoadCompression",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.netRodLoadCompression,
    label: "Net Rod Load Compression",
    decimals: 0,
    warningFlag: "netRodLoadCompressionTooHigh",
    unit: "lbs",
  },
  {
    key: "netRodLoadTension",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.netRodLoadTension,
    label: "Net Rod Load Tension",
    decimals: 0,
    warningFlag: "netRodLoadTensionTooHigh",
    unit: "lbs",
  },
  {
    key: "minDegreesReversal",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.minDegreesReversal,
    label: "Min Degrees Reversal",
    decimals: 0,
    warningFlag: "minDegreesReversalTooLow",
    unit: "degrees",
  },
  {
    key: "minNetRodLoadRatio",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.minNetRodLoadRatio,
    label: "Min Net Rod Load Ratio",
    decimals: 0,
    warningFlag: "minNetRodLoadRatioTooLow",
    unit: "%",
  },
  {
    key: "horsepowerCylinder",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.horsepowerCylinder,
    label: "Horsepower / Cylinder",
    decimals: 2,
    unit: "HP",
  },
  {
    key: "cylinderFlowMMSCFD",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.cylinderFlowMMSCFD,
    label: "Cylinder Flow",
    decimals: 2,
    unit: "mmscfd",
  },
];

/**
 * Performance flag rows: values from report detail. getValue(col) or getValue(col, warningLevel). Uses col.detail.reportDetail.
 */
export const PERFORMANCE_FLAG_ROWS = [
  {
    key: "highRodLoad",
    getValue: (col) =>
      col.detail.reportDetail.highRodLoad === true
        ? col.detail.reportDetail.highRodLoad
        : "",
    label: "High Rod Load",
    decimals: null,
    warningFlag: "highRodLoad",
    unit: "%",
  },
  {
    key: "lowMinDegreesReversal",
    getValue: (col) =>
      col.detail.reportDetail.lowMinDegreesReversal === true
        ? col.detail.reportDetail.lowMinDegreesReversal
        : "",
    label: "Low Min Degrees Reversal",
    decimals: null,
    warningFlag: "lowMinDegreesReversal",
    unit: "degrees",
  },
  {
    key: "lowMinNetRatio",
    getValue: (col) =>
      col.detail.reportDetail.lowMinNetRatio === true
        ? col.detail.reportDetail.lowMinNetRatio
        : "",
    label: "Low Min Net Ratio",
    decimals: null,
    warningFlag: "lowMinNetRatio",
    unit: "%",
  },
  {
    key: "highBlowby",
    getValue: (col, warningLevel) => {
      if (warningLevel === WARNING_LEVEL_NONE) return "";
      return ">" + (col.detail.reportDetail.blowbyThreshold ?? "");
    },
    label: "High Blowby",
    decimals: 1,
    warningFlag: "blowByThresholdWarningLevel",
    unit: "%",
  },
  {
    key: "lowVolumeEfficiency",
    getValue: (col) =>
      col.detail.reportDetail.lowVolumeEfficiency === true
        ? col.detail.reportDetail.lowVolumeEfficiency
        : "",
    label: "Low Volumetric Efficiency",
    decimals: null,
    warningFlag: "lowVolumeEfficiency",
    unit: "%",
  },
  {
    key: "highTemperature",
    getValue: (col) =>
      col.detail.reportDetail.highTemperature === true
        ? col.detail.reportDetail.highTemperature
        : "",
    label: "High Temperature",
    decimals: null,
    warningFlag: "highTemperature",
    unit: "",
  },
  {
    key: "gaugeMaintenance",
    getValue: (col) =>
      col.detail.reportDetail.gaugeMaintenance > 0
        ? col.detail.reportDetail.gaugeMaintenance
        : "",
    label: "Gauge Maintenance",
    decimals: null,
    warningFlag: "gaugeMaintenance",
    unit: "",
  },
];

/**
 * Column definitions for the "Cylinder Performance - Basic" table (BASIC_CYLINDER_ROWS).
 * Same shape as other row configs: key, getValue(col, detail), label, decimals, unit.
 * Optional: warningFlag, isSummable, hasPillStyling, applyPillStylingInTotal, pillTone ("severe" | "moderate").
 */
export const BASIC_CYLINDER_ROWS = [
  {
    key: "cylinder",
    getValue: (col) => col.label,
    label: "Cylinder",
    decimals: null,
    unit: "",
  },
  {
    key: "action",
    getValue: (col, detail) => detail.cylinder.cylinderAction ?? "",
    label: "Action",
    decimals: null,
    unit: "",
  },
  {
    key: "pocketSpacers",
    getValue: (col, detail) => {
      const fd = detail.fieldDataDetail;
      const pocket = formatValue(fd.headEndPocketAdjustment, 3);
      const he = fd.headEndSpacers ?? 0;
      const ce = fd.crankEndSpacers ?? 0;
      return `${pocket}" HE:${he} CE:${ce}`;
    },
    label: "Pocket / Spacers",
    decimals: null,
    unit: "",
  },
  {
    key: "suctPress",
    getValue: (col, detail) => detail.fieldDataDetail.suctionPressure,
    label: "Suct Press",
    decimals: 1,
    unit: "psig",
  },
  {
    key: "dischPress",
    getValue: (col, detail) => detail.fieldDataDetail.dischargePressure,
    label: "Disch Press",
    decimals: 1,
    unit: "psig",
  },
  {
    key: "cr",
    getValue: (col, detail) => detail.reportDetail.compressionRatio,
    label: "CR",
    decimals: 2,
    unit: "",
  },
  {
    key: "hp",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.horsepowerCylinder,
    label: "HP",
    decimals: 1,
    unit: "HP",
    isSummable: true,
  },
  {
    key: "flow",
    getValue: (col, detail) =>
      detail.cylinder.isDummyCylinder
        ? "n/a"
        : detail.reportDetail.cylinderFlowMMSCFD,
    label: "Flow",
    decimals: 3,
    unit: "mmscfd",
    isSummable: true,
  },
  {
    key: "blowby",
    getValue: (col, detail) => detail.reportDetail.blowby,
    label: "Blowby",
    decimals: 1,
    unit: "%",
    warningFlag: "blowbyWarningLevel",
    isSummable: true,
  },
  {
    key: "flagsSevere",
    getValue: () => 0,
    label: "Severe",
    decimals: null,
    unit: "",
    hasPillStyling: true,
    pillTone: "severe",
    isSummable: true,
    applyPillStylingInTotal: false,
  },
  {
    key: "flagsModerate",
    getValue: () => 0,
    label: "Moderate",
    decimals: null,
    unit: "",
    hasPillStyling: true,
    pillTone: "moderate",
    isSummable: true,
    applyPillStylingInTotal: false,
  },
];

/**
 * Build column descriptors for the cylinder details table from report (fieldData + reportDetails).
 * Columns are driven by fieldData.fieldDataDetail; each column's detail is
 * { stage, throw, cylinder, fieldDataDetail, reportDetail }.
 * fieldDataDetail is the fieldData.fieldDataDetail item (suction/discharge temps & pressures, pocket/spacers, cylinderAction, etc.).
 * reportDetail is the matching report.reportDetails item only (same stage/throw).
 * cylinder is nested on that item (ReportDetails.cylinder).
 *
 * @param {{ fieldData?: { fieldDataDetail?: Array<{ stage: number, throw: number, [key: string]: unknown }> }, reportDetails?: Array<{ stage: number, throw: number, cylinder: Record<string, unknown>, [key: string]: unknown }> }} report
 * @returns {Array<{ key: string, label: string, detail: { stage: number, throw: number, cylinder: Record<string, unknown>, fieldDataDetail: Record<string, unknown>, reportDetail: Record<string, unknown> } }>}
 */
export function getReportDetailsColumns(report) {
  const fieldDataDetails = report?.fieldData?.fieldDataDetail ?? [];
  const reportDetailsList = report?.reportDetails ?? [];
  const reportDetailsByStageThrow = new Map(
    reportDetailsList.map((r) => [`${r.stage}-${r.throw}`, r])
  );

  return fieldDataDetails.map((d) => {
    const stageThrow = `${d.stage}-${d.throw}`;
    const reportDetail = reportDetailsByStageThrow.get(stageThrow) ?? {};
    const cylinder = reportDetail.cylinder;
    return {
      key: `S${d.stage}-T${d.throw}`,
      label: `S${d.stage} / T${d.throw}`,
      detail: {
        stage: d.stage,
        throw: d.throw,
        cylinder,
        fieldDataDetail: d,
        reportDetail,
      },
    };
  });
}
